import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';

export default function LeaveHistory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [stats, setStats] = useState(null);
  const [leaves, setLeaves] = useState([]);

  useEffect(() => {
    if (!user) return;
    fetch(`/api/leaves/stats/${user.id}?year=${year}`)
      .then(r => r.json()).then(setStats);
    fetch(`/api/leaves?userId=${user.id}&year=${year}`)
      .then(r => r.json()).then(setLeaves);
  }, [user, year]);

  // 월별 사용 연차 계산
  const monthlyData = Array.from({ length: 12 }, (_, i) => {
    const month = String(i + 1).padStart(2, '0');
    const monthLeaves = leaves.filter(l =>
      l.status === 'approved' &&
      l.leaveType === '연차' &&
      l.dateRange?.startsWith(`${year}-${month}`)
    );
    const used = monthLeaves.reduce((s, l) => s + (l.totalDays || 0), 0);
    return { month: `${year}-${month}`, used, leaves: monthLeaves };
  });

  const totalUsed = monthlyData.reduce((s, m) => s + m.used, 0);

  return (
    <div className="page container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">연차내역</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setYear(y => y - 1)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <ChevronLeft size={20} />
          </button>
          <span style={{ fontWeight: 600, fontSize: 16 }}>{year}-01-01 ~ {year}-12-31</span>
          <button onClick={() => setYear(y => y + 1)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <ChevronRight size={20} />
          </button>
          <span style={{ fontSize: 13, color: '#9ca3af' }}>오늘</span>
        </div>
      </div>

      {/* 연차현황 요약 */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>연차현황</h2>
        <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 16 }}>
          입사일: {stats?.joinDate || '미등록'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 12, textAlign: 'center' }}>
          {[
            { label: '잔여 연차', value: `${(stats?.remainingDays || 0) - totalUsed}d`, sub: `(${((stats?.remainingDays || 0) - totalUsed) * 8}h 0m)`, color: '#4f46e5' },
            { label: '사용(신청)연차', value: `${totalUsed}d`, sub: `(${totalUsed * 8}h 0m)` },
            { label: '총 연차', value: `${stats?.totalAnnual || 0}d`, sub: `(${(stats?.totalAnnual || 0) * 8}h 0m)` },
            { label: '발생 연차', value: `${stats?.totalAnnual || 0}d`, sub: `(${(stats?.totalAnnual || 0) * 8}h 0m)` },
            { label: '이월 연차', value: '0d', sub: '(0h 0m)' },
            { label: '조정 연차', value: '0d', sub: '(0h 0m)' },
            { label: '소멸 연차', value: '0d', sub: '(0h 0m)' },
          ].map(item => (
            <div key={item.label}>
              <div style={{ fontSize: 20, fontWeight: 700, color: item.color || '#374151' }}>{item.value}</div>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>{item.sub}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 월별 연차 내역 테이블 */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>연차내역</h2>
          <button className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Download size={13} /> 엑셀 다운로드
          </button>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>연월</th>
                <th>잔여연차</th>
                <th>사용(신청)연차</th>
                <th>실사용(소진)연차</th>
                <th>총연차</th>
                <th>발생연차</th>
                <th>이월연차</th>
                <th>조정연차</th>
                <th>소멸연차</th>
              </tr>
            </thead>
            <tbody>
              {monthlyData.map((m, i) => {
                const remaining = (stats?.totalAnnual || 0) - monthlyData.slice(0, i + 1).reduce((s, x) => s + x.used, 0);
                const isCurrent = m.month === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
                return (
                  <tr key={m.month} style={{ background: isCurrent ? '#f8f7ff' : 'transparent' }}>
                    <td style={{ fontWeight: isCurrent ? 600 : 400 }}>
                      {m.month}
                      {isCurrent && <span style={{ marginLeft: 6, fontSize: 10, background: '#4f46e5', color: '#fff', padding: '1px 6px', borderRadius: 10 }}>이번달</span>}
                    </td>
                    <td style={{ color: remaining > 0 ? '#4f46e5' : '#9ca3af' }}>{remaining}d ({remaining * 8}h 0m)</td>
                    <td>{m.used}d ({m.used * 8}h 0m)</td>
                    <td>{m.used}d ({m.used * 8}h 0m)</td>
                    <td>{stats?.totalAnnual || 0}d ({(stats?.totalAnnual || 0) * 8}h 0m)</td>
                    <td>{i === 0 ? `${stats?.totalAnnual || 0}d` : '0d'}</td>
                    <td>0d (0h 0m)</td>
                    <td>0d (0h 0m)</td>
                    <td>0d (0h 0m)</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
